package com.bikompanion.ui.composables

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.bikompanion.ui.preview.PreviewSurface

/**
 * Stacked horizontal bar with a count-first legend underneath, shared by the station and parking
 * sheets so both read the same way. Each segment is drawn in proportion to [total], which may
 * exceed the sum of the segments when part of the whole is unaccounted for.
 */
@Composable
internal fun AvailabilityBar(
    segments: List<AvailabilitySegment>,
    total: Int,
    modifier: Modifier = Modifier,
) {
    if (total <= 0) return

    Column(modifier = modifier) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(24.dp)
                .clip(RoundedCornerShape(12.dp)),
        ) {
            // With nothing counted there is nothing to proportion, and a floor-weight segment each
            // would otherwise split the bar into equal slices.
            if (segments.sumOf { it.count } > 0) {
                segments.forEach { SegmentBar(it.count, total, it.color) }
            }
        }
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 8.dp),
            horizontalArrangement = if (segments.size > 1) Arrangement.SpaceBetween else Arrangement.Start,
        ) {
            segments.forEach { LegendEntry(it) }
        }
    }
}

@Composable
private fun RowScope.SegmentBar(
    count: Int,
    total: Int,
    color: Color,
) {
    // Stays composed at zero so the segment can shrink away (and grow back) when the counts change
    // under it, e.g. the sheet switching stations; `weight` only accepts positive values.
    val weight by animateFloatAsState(
        targetValue = count.toFloat() / total,
        animationSpec = tween(durationMillis = 350, easing = FastOutSlowInEasing),
        label = "SegmentWeight",
    )
    Box(
        modifier = Modifier
            .weight(weight.coerceAtLeast(MinSegmentWeight))
            .fillMaxHeight()
            .background(color),
    )
}

/** Small enough to be invisible as a sliver, large enough for `weight` to accept. */
private const val MinSegmentWeight = 0.0001f

@Composable
private fun LegendEntry(segment: AvailabilitySegment) {
    // The count leads because it's what the rider scans for; the label only says which bar segment it is.
    Column {
        AnimatedContent(
            targetState = segment.count,
            transitionSpec = {
                // The number rolls in the direction it changed, like a counter.
                val up = targetState > initialState
                (slideInVertically { if (up) it else -it } + fadeIn()) togetherWith
                    (slideOutVertically { if (up) -it else it } + fadeOut())
            },
            label = "LegendCount",
        ) { count ->
            Text(
                text = count.toString(),
                style = MaterialTheme.typography.titleLarge,
            )
        }
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .size(8.dp)
                    .clip(CircleShape)
                    .background(segment.color),
            )
            Text(
                text = segment.label,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(start = 4.dp),
            )
        }
    }
}

@Preview
@Composable
private fun AvailabilityBarPreview() {
    PreviewSurface {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            // Every segment filled.
            AvailabilityBar(
                segments = listOf(
                    AvailabilitySegment("Bikes", 7, NormalBikeColor),
                    AvailabilitySegment("Electric", 2, ElectricBikeColor),
                    AvailabilitySegment("Empty", 3, EmptySlotColor),
                ),
                total = 12,
            )
            // A single segment, whose legend sits at the start rather than spreading out.
            AvailabilityBar(
                segments = listOf(AvailabilitySegment("Capacity", 12, ParkingSpotColor)),
                total = 12,
            )
            // Part of the total unaccounted for.
            AvailabilityBar(
                segments = listOf(
                    AvailabilitySegment("Bikes", 3, NormalBikeColor),
                    AvailabilitySegment("Electric", 0, ElectricBikeColor),
                    AvailabilitySegment("Empty", 4, EmptySlotColor),
                ),
                total = 12,
            )
            // Nothing counted at all.
            AvailabilityBar(
                segments = listOf(
                    AvailabilitySegment("Bikes", 0, NormalBikeColor),
                    AvailabilitySegment("Electric", 0, ElectricBikeColor),
                    AvailabilitySegment("Empty", 0, EmptySlotColor),
                ),
                total = 12,
            )
        }
    }
}
